import { useState } from "react";
import { toast } from "sonner";
import { Copy, Loader2, PlugZap, CheckCircle2, Send, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";

const copy = (text, what) => { navigator.clipboard?.writeText(text); toast.success(`${what} copied`); };

function CopyField({ label, value, testid }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <code className="flex-1 truncate rounded-lg bg-[#FAF4E8] px-3 py-2 text-xs" data-testid={testid}>{value}</code>
        <Button size="icon" variant="outline" onClick={() => copy(value, label)} data-testid={`${testid}-copy`}><Copy className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

function TemplateRow({ t, onChanged }) {
  const [busy, setBusy] = useState("");
  const tone = t.status === "APPROVED" ? "text-emerald-700" : t.status === "REJECTED" ? "text-rose-600" : "text-amber-700";
  const test = async () => {
    setBusy("test");
    try { await api.post(`/whatsapp/test/${t.kind}`); toast.success("Test sent to your personal WhatsApp. Check your phone."); onChanged(); }
    catch (e) { toast.error(errMsg(e), { duration: 10000 }); } finally { setBusy(""); }
  };
  const mark = async () => {
    setBusy("mark");
    try { await api.put(`/whatsapp/interakt/templates/${t.name}`, { status: "APPROVED" }); onChanged(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <div className={`rounded-xl border bg-white p-3 ${t.status === "APPROVED" ? "border-[#E7E2D6]" : "border-amber-300"}`} data-testid={`interakt-tpl-${t.kind}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" onClick={() => copy(t.name, "Template name")} className="font-mono text-xs font-semibold text-[#0F382C] hover:underline">{t.name}</button>
        <span className="text-xs"><span className="text-muted-foreground">{t.category} · English · </span><span className={tone}>{t.status ? t.status.toLowerCase() : "create in Interakt"}</span></span>
      </div>
      <div className="mt-2 flex items-start gap-2">
        <p className="flex-1 text-xs leading-relaxed">{t.body}</p>
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copy(t.body, "Message text")} data-testid={`interakt-tpl-copy-${t.kind}`}><Copy className="h-3.5 w-3.5" /></Button>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={test} disabled={!!busy} data-testid={`interakt-tpl-test-${t.kind}`}>
          {busy === "test" ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Send className="mr-1 h-3 w-3" />}Send test to me
        </Button>
        {t.status !== "APPROVED" && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={mark} disabled={!!busy} data-testid={`interakt-tpl-mark-${t.kind}`}>Mark as approved</Button>}
      </div>
    </div>
  );
}

function ApprovalTracker({ templates }) {
  const approved = templates.filter((t) => t.status === "APPROVED").length;
  const missing = templates.length - approved;
  return (
    <div className="rounded-xl bg-[#FAF4E8] p-3" data-testid="interakt-approval-tracker">
      <div className="flex items-center justify-between text-sm">
        <span className="font-semibold" data-testid="interakt-approved-count">{approved} of {templates.length} templates approved</span>
        {missing > 0 && <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800" data-testid="interakt-missing-count"><BellRing className="h-3.5 w-3.5" />{missing} still to create or approve</span>}
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-white"><div className="h-full rounded-full bg-emerald-600 transition-[width] duration-500" style={{ width: `${(approved / templates.length) * 100}%` }} /></div>
      <p className="mt-2 text-xs text-muted-foreground">{missing > 0 ? "Not-approved templates are shown first. You'll get a daily reminder in the bell until all are approved. Approval updates come from Interakt's webhook; a successful test also marks a template approved." : "All messages are ready to send."}</p>
    </div>
  );
}

export function InteraktSetup({ info, onChanged }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  if (!info) return null;
  const connect = async () => {
    setBusy(true);
    try { await api.put("/whatsapp/interakt", { api_key: key }); toast.success("Interakt connected. All WhatsApp automation now uses Interakt."); setKey(""); onChanged(); }
    catch (e) { toast.error(errMsg(e), { duration: 10000 }); } finally { setBusy(false); }
  };
  const disconnect = async () => { await api.post("/whatsapp/interakt/disconnect"); toast.success("Interakt disconnected"); onChanged(); };
  return (
    <div className="mt-4 rounded-2xl border border-[#D9D2C0] p-4" data-testid="interakt-setup">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-semibold"><PlugZap className="h-4 w-4 text-[#D96B43]" />Interakt WhatsApp</div>
        {info.connected
          ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700" data-testid="interakt-status"><CheckCircle2 className="h-4 w-4" />Connected, all automation uses Interakt</span>
          : <span className="text-xs text-muted-foreground" data-testid="interakt-status">Not connected</span>}
      </div>
      {!info.connected ? (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-muted-foreground">In Interakt, open Settings → Developer Settings and copy the Secret Key. Paste it here. The app checks it with Interakt before saving.</p>
          <div className="flex gap-2">
            <Input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="Interakt Secret Key" data-testid="interakt-key-input" />
            <Button onClick={connect} disabled={busy || !key.trim()} className="bg-[#0F382C] hover:bg-[#134E3A]" data-testid="interakt-connect-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Connect"}</Button>
          </div>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-muted-foreground">Step 1: in Interakt → Settings → Developer Settings → Webhooks, paste this URL and secret, and tick these events: message status (sent, delivered, read, failed), template status, and message received (needed so customers who reply STOP are opted out). Growth plan or higher.</p>
          <CopyField label="Webhook URL" value={info.webhook_url} testid="interakt-webhook-url" />
          {info.webhook_secret && <CopyField label="Webhook secret" value={info.webhook_secret} testid="interakt-webhook-secret" />}
          <p className="text-xs text-muted-foreground">Step 2: in Interakt → Templates, create each template below with exactly this name, category and text. Use sample values for the variables. Each message starts working as soon as Meta approves it.</p>
          <ApprovalTracker templates={info.templates} />
          <div className="max-h-96 space-y-2 overflow-y-auto pr-1" data-testid="interakt-templates">
            {[...info.templates].sort((a, b) => (a.status === "APPROVED") - (b.status === "APPROVED")).map((t) => <TemplateRow key={t.kind} t={t} onChanged={onChanged} />)}
          </div>
          <Button size="sm" variant="ghost" onClick={disconnect} className="text-rose-600" data-testid="interakt-disconnect-btn">Disconnect Interakt</Button>
        </div>
      )}
    </div>
  );
}
